import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import {
    PushNotifications,
    Token,
    PushNotificationSchema,
    ActionPerformed,
} from '@capacitor/push-notifications';
import { environment } from 'src/environments/environment';
import { NativeNotificationService } from './native-notification.service';
import { PersistentDevice } from '../plugins/persistent-device.plugin';

@Injectable({
    providedIn: 'root',
})
export class PushTokenService {
    private initialized = false;
    private currentToken = '';
    private currentDeviceUuid = '';
    private waitingForFirebaseToken = false;

    constructor(
        private http: HttpClient,
        private router: Router,
        private nativeNotifications: NativeNotificationService,
        private ngZone: NgZone
    ) {}

    isNativeDevice(): boolean {
        return Capacitor.isNativePlatform();
    }

    async init(): Promise<void> {
        if (!this.isNativeDevice() || this.initialized) {
            return;
        }

        this.initialized = true;

        await PushNotifications.addListener('registration', (token: Token) => {
            if (!this.shouldSyncToken(token.value)) {
                if (Capacitor.getPlatform() === 'ios') {
                    this.waitingForFirebaseToken = true;
                    console.log(
                        'Push registration received APNs token; waiting for Firebase Messaging token before syncing.'
                    );
                }
                return;
            }

            this.waitingForFirebaseToken = false;
            this.currentToken = token.value;
            this.registerTokenWithBackend().catch((error) => {
                console.error('Failed to sync push token:', error);
            });
        });

        await PushNotifications.addListener('registrationError', (error: any) => {
            console.error('Push registration failed:', error);
        });

        await PushNotifications.addListener(
            'pushNotificationReceived',
            (notification: PushNotificationSchema) => {
                this.handleIncomingPush(notification);
            }
        );

        await PushNotifications.addListener(
            'pushNotificationActionPerformed',
            (action: ActionPerformed) => {
                const data = action?.notification?.data || {};
                console.log(
                    `[PushRoute] Tap received. dataKeys=${Object.keys(data).join(',') || '<none>'}`
                );
                this.handlePushAction(action);
            }
        );
        console.log('[PushRoute] Push notification action listener registered.');
    }

    async registerCurrentDevice(): Promise<void> {
        if (!this.isNativeDevice()) {
            return;
        }

        const lastRegistrationAt = Number(localStorage.getItem('connect_push_registration_at') || 0);
        if (lastRegistrationAt && Date.now() - lastRegistrationAt < 6 * 60 * 60 * 1000) {
            return;
        }

        await this.init();

        const permission = await PushNotifications.requestPermissions();
        if (permission.receive !== 'granted') {
            console.error('Push notification permission not granted');
            return;
        }

        if (this.currentToken) {
            this.registerTokenWithBackend().catch((error) => {
                console.error('Failed to sync cached push token:', error);
            });
        }

        await PushNotifications.register();
        localStorage.setItem('connect_push_registration_at', Date.now().toString());

        if (Capacitor.getPlatform() === 'ios') {
            setTimeout(() => {
                if (this.waitingForFirebaseToken) {
                    console.warn(
                        'Firebase Messaging token was not emitted after APNs registration; /mobile-device-token will not be called until iOS provides the FCM token.'
                    );
                }
            }, 5000);
        }
    }

    async unregisterCurrentDevice(): Promise<void> {
        if (!this.isNativeDevice()) {
            return;
        }

        if (this.currentToken) {
            try {
                await this.http
                    .delete(`${environment.api_url}/mobile-device-token`, {
                        body: { device_token: this.currentToken },
                    })
                    .toPromise();
            } catch (error) {
                console.error('Failed to delete push token:', error);
            }
        }

        try {
            await PushNotifications.unregister();
        } catch (error) {
            console.error('Failed to unregister push notifications:', error);
        }

        this.waitingForFirebaseToken = false;
    }

    private async registerTokenWithBackend(): Promise<void> {
        if (!this.currentToken) {
            return;
        }

        const deviceUuid = await this.getDeviceUuid();

        const payload = {
            platform: Capacitor.getPlatform(),
            device_token: this.currentToken,
            device_uuid: deviceUuid,
            device_name: navigator.userAgent || 'mobile-device',
            app_version: 'mobile',
        };

        await this.http.post(`${environment.api_url}/mobile-device-token`, payload).toPromise();
    }

    private async getDeviceUuid(): Promise<string | null> {
        if (this.currentDeviceUuid) {
            return this.currentDeviceUuid;
        }

        try {
            if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
                const { identifier } = await PersistentDevice.getId();
                this.currentDeviceUuid = identifier || '';
                return this.currentDeviceUuid || null;
            }

            const { identifier } = await Device.getId();
            this.currentDeviceUuid = identifier || '';
            return this.currentDeviceUuid || null;
        } catch (error) {
            console.error('Failed to get device identifier:', error);
            return null;
        }
    }

    private shouldSyncToken(token: string): boolean {
        if (Capacitor.getPlatform() !== 'ios') {
            return true;
        }

        // Firebase Messaging will publish the FCM token shortly after APNs registration.
        // Ignore the raw APNs hex token so the backend stores the token format Firebase expects.
        return !/^[A-F0-9]{64,}$/i.test(token);
    }

    private handleIncomingPush(notification: PushNotificationSchema): void {
        const data = notification?.data || {};
        const action = `${data['action'] || ''}`.toLowerCase();
        const type = `${data['type'] || ''}`.toLowerCase();
        const body = notification?.body || `${data['body'] || data['message'] || ''}`;

        if (action === 'clear_alerts') {
            this.nativeNotifications.clearAlertNotifications();
            return;
        }

        if (type === 'call') {
            return;
        }

        if (type === 'email') {
            this.nativeNotifications.notifyAlert(
                body || 'New email received',
                'email',
                this.getConversationId(data)
            );
            return;
        }

        if (type === 'sms') {
            this.nativeNotifications.notifyAlert(
                body || 'New text received',
                'sms',
                this.getConversationId(data)
            );
        }
    }

    private handlePushAction(action: ActionPerformed): void {
        const data = action?.notification?.data || {};
        const type = `${data['type'] || ''}`.toLowerCase();
        const route = `${data['route'] || ''}`;
        const conversationId = this.getConversationId(data);

        console.log(
            `[PushRoute] Handling notification tap. type=${type || '<none>'}, route=${route || '<none>'}, conversationId=${conversationId || '<none>'}`
        );

        if (conversationId) {
            this.navigateToConversation(conversationId);
            return;
        }

        if (route) {
            this.router.navigate([route]).catch((error) => {
                console.error('Failed to route push action:', error);
            });
            return;
        }

        if (type === 'call') {
            this.router.navigate(['conversations']).catch((error) => {
                console.error('Failed to route call push action:', error);
            });
            return;
        }

        this.router.navigate(['alerts']).catch((error) => {
            console.error('Failed to route alert push action:', error);
        });
    }

    private parseConversationId(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    private getConversationId(payload: any): number | null {
        const sources = [
            payload,
            payload?.data,
            payload?.extra,
            payload?.data?.data,
        ];

        for (const source of sources) {
            const conversationId = this.parseConversationId(
                source?.conversation_id ??
                source?.conversationId ??
                source?.ConversationId ??
                source?.conversation?.id
            );
            if (conversationId) {
                return conversationId;
            }
        }

        return null;
    }

    private navigateToConversation(conversationId: number): void {
        this.ngZone.run(() => {
            console.log(`[PushRoute] Navigating to conversation ${conversationId}.`);
            this.router
                .navigate(['conversations'], {
                    queryParams: { id: conversationId },
                })
                .then((navigated) => {
                    console.log(
                        `[PushRoute] Navigation result=${navigated}, currentUrl=${this.router.url}`
                    );
                })
                .catch((error) => {
                    console.error('[PushRoute] Failed to route push action to conversation:', error);
                });
        });
    }
}
